import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  XAxis,
  YAxis,
} from "recharts";

import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/v2/chart";
import { useValueFormatter } from "@/utils/useValueFormatter";

const PALETTE = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
];

export interface SegmentSeries {
  id: string;
  name: string;
}

interface ChartRowDatum {
  day: string;
  [segmentId: string]: number | string | null;
}

interface Props {
  rows: ChartRowDatum[];
  chartConfig: ChartConfig;
  visibleSegments: SegmentSeries[];
  formatDay: (day: string) => string;
  drag: { left: string; right: string };
  onMouseDown: (e: { activeLabel?: string | number }) => void;
  onMouseMove: (e: { activeLabel?: string | number }) => void;
  onMouseUp: () => void;
}

export function SegmentCountChartBody({
  rows,
  chartConfig,
  visibleSegments,
  formatDay,
  drag,
  onMouseDown,
  onMouseMove,
  onMouseUp,
}: Props) {
  const { format } = useValueFormatter();

  return (
    <ChartContainer
      config={chartConfig}
      className="aspect-auto h-[280px] w-full"
    >
      <LineChart
        data={rows}
        margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
        style={{
          userSelect: "none",
          cursor: drag.left ? "crosshair" : "default",
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="day"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          interval="preserveStartEnd"
          minTickGap={32}
          tickFormatter={formatDay}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          allowDecimals={false}
          tickFormatter={(value) => format(Number(value), "whole")}
        />
        <ChartTooltip
          allowEscapeViewBox={{ x: false, y: true }}
          content={
            <ChartTooltipContent
              indicator="dot"
              labelFormatter={(day) => formatDay(String(day))}
              className="max-h-[220px] min-w-[170px] overflow-y-auto"
            />
          }
        />
        <ChartLegend
          content={
            <ChartLegendContent className="grid-cols-2 sm:grid-cols-3 md:grid-cols-4" />
          }
        />
        {visibleSegments.map((s, i) => {
          const color = PALETTE[i % PALETTE.length];
          return (
            <Line
              key={s.id}
              type="monotone"
              dataKey={s.id}
              stroke={color}
              strokeWidth={2}
              dot={{ r: 3, stroke: color, fill: color }}
              activeDot={{ r: 5, stroke: color, fill: color }}
              isAnimationActive={false}
              connectNulls={false}
            />
          );
        })}
        {drag.left && drag.right ? (
          <ReferenceArea
            x1={drag.left}
            x2={drag.right}
            fillOpacity={0.10}
            fill="var(--color-primary)"
            stroke="var(--color-primary)"
            strokeOpacity={1}
            strokeWidth={1}
          />
        ) : null}
      </LineChart>
    </ChartContainer>
  );
}

export { PALETTE as SEGMENT_CHART_PALETTE };
